import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Clientele = () => {
  const { t } = useTranslation('clientele');
  const { language } = useLanguage();

  const sectors = [
    {
      name: t('sectors.government.name'),
      count: t('sectors.government.count'),
      tag: t('sectors.government.tag')
    },
    {
      name: t('sectors.manufacturing.name'),
      count: t('sectors.manufacturing.count'),
      tag: t('sectors.manufacturing.tag')
    },
    {
      name: t('sectors.financial.name'),
      count: t('sectors.financial.count'),
      tag: t('sectors.financial.tag')
    },
    {
      name: t('sectors.healthcare.name'),
      count: t('sectors.healthcare.count'),
      tag: t('sectors.healthcare.tag')
    },
    {
      name: t('sectors.technology.name'),
      count: t('sectors.technology.count'),
      tag: t('sectors.technology.tag')
    },
    {
      name: t('sectors.energy.name'),
      count: t('sectors.energy.count'),
      tag: t('sectors.energy.tag')
    }
  ];

  const clientLogos = [
    'ewands-clientel-marriott.png',
    'ewands-clientel-daikin.png',
    'ewands-clientel-UEMSunrise.png',
    'ewands-clientel-petronas.png',
    'ewands-clientel-prudential.png',
    'ewands-clientel-suriaklcc.png',
    'ewands-clientel-citibank.png',
    'ewands-clientel-exytes.jpg',
    'ewands-clientel-sanovo.jpg',
    'ewands-clientel-a2-automation.jpg',
    'ewands-clientel-MalaysianGASAssociation.png',
    'ewands-clientel-joeyyap.png'
  ];

  return (
    <div className="clientele-page">
      <SEO 
        title="Our Clients - Trusted by Leading Malaysian Enterprises"
        description="EWANDS serves leading organizations across government, manufacturing, financial services, healthcare, technology, and energy sectors in Malaysia including Petronas, Marriott, Citibank, and more."
        keywords="EWANDS clients Malaysia, AI clients Malaysia, enterprise AI customers, Malaysia AI case studies, government AI projects, banking AI solutions"
        canonicalPath={`/${language}/clientele`}
      />
      {/* Hero Section */}
      <section className="hero-section clientele-hero">
        <div className="hero-background">
          <div className="gradient-layer"></div>
          <div className="particle-layer"></div>
          <div className="grid-overlay"></div>
          <div className="tech-lines"></div>
        </div>
        
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-2"></div>
              <div className="tech-element tech-element-7"></div>
              <h1 className="hero-quote">
                <span className="quote-highlight">{t('hero.title')}</span><br />
                {t('hero.subtitle1')}<br />
                {t('hero.subtitle2')}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Clientele Content */}
      <section className="capabilities-grid-section">
        <div className="container">
          <div className="row g-4 mb-5">
            {sectors.map((sector, index) => (
              <div key={index} className="col-lg-6 col-xl-4">
                <div className="capability-card-modern fade-in" style={{ animationDelay: `${0.3 + index * 0.1}s`, textAlign: 'center' }}>
                  <div className="card-tag">{sector.tag}</div>
                  <h2 className="capability-title-bold" style={{ color: 'var(--brand-primary)', fontSize: '3rem', marginBottom: '0.5rem' }}>{sector.count}</h2>
                  <h3 className="capability-title-bold" style={{ fontSize: '1.5rem' }}>{sector.name}</h3>
                  <div className="card-divider" style={{ margin: '1rem auto' }}></div>
                </div>
              </div>
            ))}
          </div>

          {/* Client Logos Grid */}
          <div className="client-logos-section mt-5 pt-5">
            {/* Full Width Background Section */}
            <div className="client-logos-fullwidth-wrapper">
              <div className="container">
                <div className="text-center mb-5 fade-in">
                  <div className="card-tag">{t('logos.tag')}</div>
                  <h2 className="capability-title-bold">{t('logos.heading')}</h2>
                  <div className="card-divider" style={{ margin: '0 auto' }}></div>
                </div>
                
                <div className="row g-5 justify-content-center">
                  {clientLogos.map((logo, index) => (
                    <div key={index} className="col-6 col-md-4 col-lg-3 col-xl-3">
                      <div className="client-logo-wrapper fade-in" style={{ animationDelay: `${0.5 + index * 0.05}s` }}>
                        <img 
                          src={`/Images/clientele/${logo}`} 
                          alt={`Client ${index + 1}`} 
                          className="client-logo-img"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Full Width Partnership CTA Section */}
      <section className="clientele-partnership-cta-section" style={{
        width: '100%',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
        borderTop: '1px solid rgba(59, 130, 246, 0.2)',
        borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
        padding: '5rem 0'
      }}>
        <div className="container">
          <div className="capability-card-modern fade-in" style={{ 
            textAlign: 'center', 
            animationDelay: '0.8s',
            background: 'transparent',
            border: 'none',
            padding: '2rem',
            boxShadow: 'none'
          }}>
            <div className="card-tag" style={{ background: 'transparent', border: 'none', padding: '0.5rem 0' }}>{t('cta.tag')}</div>
            <h2 className="capability-title-bold" style={{ textAlign: 'center' }}>{t('cta.title')}</h2>
            <div className="card-divider" style={{ margin: '0 auto 2rem' }}></div>
            <p className="capability-desc-text" style={{ maxWidth: '900px', margin: '0 auto' }}>
              {t('cta.description')}
            </p>
            <div style={{ marginTop: '3rem', marginBottom: '3rem', display: 'flex', justifyContent: 'center' }}>
              <Link to={`/${language}/contact`} className="hero-cta" style={{ 
                fontSize: '1.25rem',
                padding: '1.5rem 3rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1.5rem',
                border: '2px solid',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                backdropFilter: 'blur(10px)'
              }}>
                <span className="cta-chevrons" style={{ margin: 0 }}>
                  <span>›</span><span>›</span><span>›</span>
                </span>
                {t('cta.button')}
                <span className="cta-chevrons-reverse" style={{ margin: 0 }}>
                  <span>‹</span><span>‹</span><span>‹</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Clientele;
